import { useContext, useMemo } from 'react';
import { GenericSimulatorContext } from '../context';

/**
 * Adapter hook that exposes GenericSimulator context data in the shape
 * expected by the console components (scenario, selected_element, simulation_time).
 */
const useDashboard = () => {
    const context = useContext(GenericSimulatorContext);

    if (context === undefined) {
        throw new Error('useDashboard must be used within a GenericSimulatorProvider');
    }

    const { deploymentData, selectedElement } = context;

    /** scenario data (mirrors deploymentData.data) */
    const scenario = useMemo(() => deploymentData?.data ?? null, [deploymentData]);

    /**
     * selected_element — the console components expect { trueTwinId, elementId }.
     * We derive them from the first element in deployment data when no element is
     * explicitly selected, so the console always has something to query against.
     */
    const selected_element = useMemo(() => {
        // If user has selected an element in the viewer, try to match it
        if (selectedElement?.elementId && scenario?.elements?.length) {
            const match = scenario.elements.find(
                (el: any) => el.elementId === selectedElement.elementId
            );
            if (match) {
                return {
                    trueTwinId: match.trueTwinId ?? match.truetwinId ?? '',
                    elementId: match.elementId ?? '',
                };
            }
        }
        // Fall back to the first element in scenario
        if (scenario?.elements?.[0]) {
            const first = scenario.elements[0];
            return {
                trueTwinId: first.trueTwinId ?? first.truetwinId ?? '',
                elementId: first.elementId ?? '',
            };
        }
        return null;
    }, [selectedElement, scenario]);

    /**
     * simulation_time — start / end / duration derived from scenario timing.
     * start_time & end_time are epoch seconds; duration is in minutes.
     */
    const simulation_time = useMemo(() => {
        if (!scenario) return null;
        const start = scenario.scenarioStartTime ?? null;
        const duration = scenario.scenarioDuration ?? null; // minutes
        if (start == null || duration == null) return null;
        return {
            start_time: start,
            end_time: start + duration * 60,
            duration,
        };
    }, [scenario]);

    return {
        scenario,
        selected_element,
        simulation_time,
    };
};

export default useDashboard;
